import {
  ClipboardCheck,
  Factory,
  Gauge,
  ShieldCheck,
  Building2,
} from "lucide-react";
import SEO from "../components/site/SEO";
import {
  CTASection,
  HighlightList,
  InfoCard,
  MetricCard,
  PageHero,
  SectionHeader,
  ServiceCard,
  TestimonialCard,
} from "../components/site/Sections";
import {
  companyInfo,
  condominiumSolutions,
  differentials,
  heroSlides,
  homeSeo,
  imageLibrary,
  operationalStats,
  primaryServices,
  testimonials,
} from "../content/siteContent";

const icons = [Building2, Gauge, ClipboardCheck, Factory, ShieldCheck];

const HomePage = () => (
  <>
    <SEO
      title={homeSeo.title}
      description={homeSeo.description}
      canonicalPath="/"
      keywords={[
        "manutenção de cabine primária em São Paulo",
        "ensaios elétricos em subestação",
        "comissionamento de painéis elétricos",
        "manutenção elétrica industrial",
      ]}
    />

    <PageHero
      eyebrow="W-Electric Engenharia Elétrica"
      title="Confiabilidade e Segurança em Sistemas Elétricos"
      description="Especialistas em manutenção de cabine primária, subestações e ensaios elétricos em São Paulo para condomínios, indústrias e empresas que não podem correr riscos operacionais."
      image={imageLibrary.hero}
      images={heroSlides}
      ctaMessage="Olá, gostaria de solicitar um orçamento para manutenção elétrica em São Paulo."
      testId="home-hero"
    />

    <section className="container-shell py-6 md:py-10" data-testid="home-stats-section">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {operationalStats.map((stat) => (
          <MetricCard
            key={stat.label}
            value={stat.value}
            label={stat.label}
            testId={`home-stat-${stat.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          />
        ))}
      </div>
    </section>

    <section className="container-shell py-14 md:py-20" data-testid="home-services-section">
      <SectionHeader
        eyebrow="Serviços principais"
        title="Soluções técnicas para ativos críticos"
        description="Atuação completa para manutenção, testes, comissionamento e suporte técnico em sistemas elétricos de média e baixa tensão."
        testId="home-services-header"
      />
      <div className="mt-10 technical-grid">
        {primaryServices.map((service, index) => (
          <ServiceCard key={service.title} service={service} index={index} />
        ))}
      </div>
    </section>

    <section className="container-shell py-14 md:py-20" data-testid="home-condominiums-section">
      <div className="grid gap-8 lg:grid-cols-[0.92fr_1.08fr] lg:items-start">
        <InfoCard
          title="Serviços para condomínios"
          description="Estruturamos rotinas de manutenção e adequação elétrica para síndicos, administradoras e facilities que precisam reduzir riscos e manter a infraestrutura confiável."
          items={["Manutenção preventiva", "Termografia", "Laudos e ARTs", "Wallbox", "Modernização de quadros"]}
          testId="home-condominiums-card"
          dark
        />

        <div className="grid gap-5">
          {condominiumSolutions.map((item, index) => {
            const Icon = icons[index] || ShieldCheck;

            return (
              <div
                className="surface-card p-6 transition duration-500 hover:-translate-y-2 hover:shadow-[0_40px_92px_rgba(11,31,58,0.2)] md:p-7"
                key={item.title}
                data-testid={`condominium-solution-${index + 1}`}
              >
                <div className="flex flex-col gap-5 md:flex-row md:items-start">
                  <div className="inline-flex h-12 w-12 items-center justify-center bg-brand text-safety" data-testid={`condominium-solution-icon-${index + 1}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-heading text-3xl uppercase text-brand" data-testid={`condominium-solution-title-${index + 1}`}>
                      {item.title}
                    </h3>
                    <p className="body-copy" data-testid={`condominium-solution-description-${index + 1}`}>
                      {item.description}
                    </p>
                    {item.badges ? (
                      <div className="flex flex-wrap gap-3" data-testid={`condominium-solution-badges-${index + 1}`}>
                        {item.badges.map((badge) => (
                          <span
                            key={badge}
                            className="border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-brand"
                            data-testid={`condominium-solution-badge-${badge.toLowerCase()}`}
                          >
                            {badge}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    <section className="bg-brand py-14 md:py-20" data-testid="home-differentials-section">
      <div className="container-shell grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div className="space-y-6">
          <SectionHeader
            eyebrow="Diferenciais"
            title="Estrutura técnica para operar com precisão"
            description={`A ${companyInfo.shortName} entrega atendimento técnico, documentação clara e execução responsável para sistemas elétricos que exigem alto padrão de segurança.`}
            light
            testId="home-differentials-header"
          />
        </div>

        <HighlightList items={differentials} testId="home-differentials-list" light />
      </div>
    </section>

    <section className="container-shell py-14 md:py-20" data-testid="home-testimonials-section">
      <SectionHeader
        eyebrow="Depoimentos"
        title="Confiança construída em campo"
        description="Exemplos de como a atuação técnica e a comunicação objetiva ajudam clientes corporativos a manter seus sistemas elétricos sob controle."
        testId="home-testimonials-header"
      />
      <div className="mt-10 grid gap-6 lg:grid-cols-3">
        {testimonials.map((item, index) => (
          <TestimonialCard key={item.company} item={item} index={index} />
        ))}
      </div>
    </section>

    <CTASection
      title="Pronto para proteger sua operação elétrica?"
      description="Solicite um orçamento e converse com uma equipe preparada para atender condomínios, indústrias e empresas em São Paulo."
      buttonLabel="Solicitar orçamento"
      buttonMessage="Olá, gostaria de solicitar um orçamento para manutenção elétrica em São Paulo."
      testId="home-final-cta"
    />
  </>
);

export default HomePage;